/**
 * A schema for changing some of a record's fields.
 *
 * Zod 4's `.partial()` still fills a missing field from its `.default()`, so a
 * patch that sends one field ("mark inactive") silently resets every other
 * defaulted field — a location's access notes, a product's description. This
 * drops the defaults: a field left out of the patch stays exactly as it is.
 */
import { z } from 'zod'

type WithoutDefault<S> = S extends z.ZodDefault<infer Inner> ? Inner : S

export function patchOf<Shape extends z.ZodRawShape>(schema: z.ZodObject<Shape>) {
  const shape = Object.fromEntries(
    Object.entries(schema.shape).map(([key, field]) => [
      key,
      z.optional(field instanceof z.ZodDefault ? field.unwrap() : field),
    ]),
  ) as { [K in keyof Shape]: z.ZodOptional<WithoutDefault<Shape[K]>> }
  return z.object(shape)
}
